import type { NovelItem } from '@app/_proto/Protos/novels';
import { GetPlacementEntriesRequest, type GetPlacementEntriesResponse } from '@app/_proto/Protos/placements';
import { PlacementsClient } from '@app/_proto/Protos/placements.client';
import { useGrpcApiWithQuery } from '@app/libs/api';
import { getOneofValue, useGrpcRequest } from '@app/libs/grpc';
import { useMemo } from 'react';

type UsePlacementEntriesParam = {
    placementId: number;
    enabled?: boolean;
    suspense?: boolean;
    staleTime?: number;
};

type UsePlacementEntriesReturn = {
    data: NovelItem[] | undefined;
    originData: GetPlacementEntriesResponse | undefined;
    isLoading: boolean;
    isSuccess: boolean;
};

export default function usePlacementEntries({
    placementId,
    enabled,
    suspense,
    staleTime,
}: UsePlacementEntriesParam): UsePlacementEntriesReturn {
    const getPlacementEntriesRequest = useGrpcRequest(GetPlacementEntriesRequest, {
        placementId,
    });
    const {
        data: originData,
        isLoading,
        isSuccess,
    } = useGrpcApiWithQuery(
        PlacementsClient,
        c => c.getPlacementEntries,
        getPlacementEntriesRequest,
        ['placement-entries', placementId],
        {
            enabled,
            suspense,
            cacheTime: 1000 * 60 * 60 * 24, // 24 hours cache
            staleTime: staleTime ?? 1000 * 60 * 60 * 24, // Data stales in 24 hours
            showModalOnError: true,
        }
    );
    const novels = useMemo(() => {
        return originData?.items.map(r => getOneofValue(r.series!.item, 'novel')!);
    }, [originData?.items]);

    return { data: novels, originData, isLoading, isSuccess };
}
